/**
 * Panier heycup — écran 2b.
 *
 * Deux responsabilités, et rien d'autre : verrouiller la quantité de ligne,
 * et afficher ce que la ligne représente vraiment — un lot de N pièces, avec
 * le palier suivant s'il fait baisser le prix.
 *
 * Le verrouillage passe par `min == max` sur l'entrée, et non par
 * `can_update_quantity` : `component-cart-quantity-selector.js` réécrit
 * l'état des deux boutons à chaque mise à jour, uniquement d'après
 * min / value / max. Les boutons ne sont pas retirés du DOM — le composant
 * les déclare en refs obligatoires et lèverait une exception qui casserait
 * l'ensemble du panier.
 */

import { formaterPieces } from './hey-grille.js';

const SEL_LIGNE = '.cart-items__table-row';
const SEL_QTE = 'input[data-cart-line]';

class HeyPanier extends HTMLElement {
  #lignes = {};
  #enAttente = false;
  #corrections = new Set();

  connectedCallback() {
    const balise = this.querySelector('[data-hey-panier-lignes]');
    if (balise) {
      try {
        this.#lignes = JSON.parse(balise.textContent) || {};
      } catch (e) {
        // On continue sans donnees plutot que d'abandonner : l'observateur
        // doit s'installer quoi qu'il arrive, sinon un panier re-rendu ne
        // serait plus jamais traite.
        console.error('[heycup] données de panier illisibles', e);
        this.#lignes = {};
      }
    }

    this.#appliquer();

    // Le panier se re-rend par l'API de rendu de section : on repasse après
    // chaque remplacement de noeud plutôt que d'écouter un événement précis,
    // dont le nom peut changer d'une version d'Horizon à l'autre.
    this.observateur = new MutationObserver(() => this.#planifier());
    const cible = document.querySelector('cart-items-component') || document.body;
    this.observateur.observe(cible, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observateur?.disconnect();
  }

  #planifier() {
    if (this.#enAttente) return;
    this.#enAttente = true;
    requestAnimationFrame(() => {
      this.#enAttente = false;
      this.#appliquer();
    });
  }

  #appliquer() {
    for (const entree of document.querySelectorAll(SEL_QTE)) {
      const ligne = entree.closest(SEL_LIGNE);
      if (!ligne) continue;

      const info = this.#lignes[this.#idVariante(ligne)];
      if (!info) continue; // ligne hors catalogue heycup : on n'y touche pas

      this.#verrouiller(entree);
      this.#afficherPieces(ligne, info);
      this.#afficherSuivant(ligne, info);
    }

    // Nos propres ecritures viennent de remplir la file de l'observateur.
    // Sans cette purge, chaque passe se redeclencherait elle-meme et la
    // boucle ne convergerait jamais.
    this.observateur?.takeRecords();
  }

  /** L'identifiant de variante, lu sur le lien produit de la ligne. */
  #idVariante(ligne) {
    const lien = ligne.querySelector('a[href*="variant="]');
    if (!lien) return null;
    const m = lien.getAttribute('href').match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  #verrouiller(entree) {
    // Toujours 1, jamais la valeur trouvee. Figer une ligne deja a 2 aurait
    // fige l'erreur : deux mille pieces facturees au double du prix du palier
    // mille. Shopify fusionne deux ajouts identiques en une ligne de
    // quantite 2, donc l'etat est atteignable sans rien forcer.
    entree.min = '1';
    entree.max = '1';
    entree.readOnly = true;
    entree.setAttribute('data-min', '1');
    entree.setAttribute('aria-readonly', 'true');
    entree.tabIndex = -1;

    // Le composant relit min/max au prochain rendu ; en attendant, on aligne
    // l'état visible des boutons pour éviter une frame cliquable.
    // Une ligne deja au-dela de 1 est un etat invalide : on la ramene.
    if (entree.value && entree.value !== '1') this.#ramenerAUn(entree);

    const controles = entree.closest('.cart-items__quantity-controls');
    controles?.classList.add('hey-panier__quantite-verrouillee');
    for (const b of controles?.querySelectorAll('button[ref], quantity-selector-component button') || []) {
      if (b.classList.contains('cart-items__remove')) continue; // la corbeille reste active
      b.disabled = true;
    }
  }

  /**
   * Ramene une ligne a la quantite 1. Le prix d'une variante est celui du LOT :
   * une ligne a 2 facture deux lots, ce que le cahier interdit explicitement.
   */
  async #ramenerAUn(entree) {
    const ligne = entree.dataset.cartLine;
    if (!ligne || this.#corrections.has(ligne)) return;
    this.#corrections.add(ligne); // une seule tentative par ligne

    try {
      await fetch(window.Theme?.routes?.cart_change_url
        ? `${window.Theme.routes.cart_change_url}.js`
        : '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(ligne), quantity: 1 }),
      });
      window.location.reload();
    } catch (e) {
      console.error('[heycup] quantité de ligne non ramenée à 1', e);
    }
  }

  #afficherPieces(ligne, info) {
    const controles = ligne.querySelector('.cart-items__quantity-controls');
    if (!controles) return;

    let n = controles.querySelector('.hey-panier__pieces');
    if (!n) {
      n = document.createElement('p');
      n.className = 'hey-panier__pieces';
      controles.prepend(n);
    }
    // Construit en noeuds plutôt qu'en `innerHTML` : les libellés viennent de
    // réglages de section, donc d'une saisie humaine, et n'ont rien à faire
    // dans un analyseur HTML.
    const attendu = [
      formaterPieces(info.pieces),
      this.dataset.libellePieces || 'pièces',
      info.unitaire ? `${info.unitaire} la pièce` : '',
    ].join('|');
    if (n.dataset.heyEtat === attendu) return; // rien n'a change
    n.dataset.heyEtat = attendu;

    n.textContent = '';
    n.append(
      balise('span', 'hey-panier__pieces-nombre', formaterPieces(info.pieces)),
      ' ',
      balise('span', 'hey-panier__pieces-unite', this.dataset.libellePieces || 'pièces')
    );
    if (info.unitaire) {
      n.append(balise('span', 'hey-panier__pieces-prix', `${info.unitaire} la pièce`));
    }
  }

  #afficherSuivant(ligne, info) {
    const hote = ligne.querySelector('.cart-items__variants-wrapper') || ligne;
    let n = hote.querySelector('.hey-panier__suivant');

    if (!info.suivant) {
      n?.remove();
      return;
    }

    if (!n) {
      n = document.createElement('p');
      n.className = 'hey-panier__suivant';
      hote.append(n);
    }

    const texte = (this.dataset.modeleSuivant || '')
      .replace('[palier]', formaterPieces(info.suivant.pieces))
      .replace('[prix]', info.suivant.unitaire)
      .replace('[prix_actuel]', info.unitaire)
      .replace('[total]', info.suivant.total)
      .replace('[economie]', String(info.suivant.economie));

    const libelleBouton = (this.dataset.libellePasser || 'Passer à [palier]').replace(
      '[palier]',
      formaterPieces(info.suivant.pieces)
    );

    if (n.dataset.heyEtat === texte + libelleBouton) return; // rien n'a change
    n.dataset.heyEtat = texte + libelleBouton;

    n.textContent = '';
    const span = document.createElement('span');
    span.textContent = texte;
    const lien = document.createElement('a');
    lien.className = 'hey-lien hey-lien--souligne hey-panier__suivant-lien';
    lien.href = info.suivant.url;
    lien.textContent = libelleBouton;
    n.append(span, ' ', lien);
  }
}

/** Un élément avec sa classe et son texte, sans passer par l'analyseur HTML. */
function balise(nom, classe, texte) {
  const el = document.createElement(nom);
  el.className = classe;
  el.textContent = texte;
  return el;
}

if (!customElements.get('hey-panier')) {
  customElements.define('hey-panier', HeyPanier);
}
